/** Графики вкладки: заказы по месяцам (бар) + доли каналов (донат) + «Инсайты».
 * Порт drawBars/drawDonut/renderInsights (build-orders-dashboard-html.js,
 * стр. 4399-4592) на recharts. Палитра каналов валидирована dataviz-скриптом:
 * light #55b9dd/#7467d8/#58a876 (WARN контраста снят легендой, прямыми подписями
 * и таблицей-матрицей ниже), dark #1f9ecb/#7467d8/#58a876 — все проверки PASS. */
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { MpCode } from "@/api/orders";
import { axisTick } from "@/lib/chart-theme";
import { useIsDark } from "@/lib/use-is-dark";
import { cn } from "@/lib/utils";
import { int0, monthFullLabel, monthLabel, signed } from "./format";
import { rowMetrics, type ViewContext, type ViewRow } from "./metrics";

/** Фиксированный порядок категорий — хук за сущностью, не за рангом. */
const CHANNELS: { key: MpCode; name: string; light: string; dark: string }[] = [
  { key: "ozon", name: "Ozon", light: "#55b9dd", dark: "#1f9ecb" },
  { key: "wb", name: "Wildberries", light: "#7467d8", dark: "#7467d8" },
  { key: "ym", name: "Яндекс.Маркет", light: "#58a876", dark: "#58a876" },
];

function ChartCard({
  title,
  sub,
  children,
  className,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn("rounded-md border bg-card p-3", className)}>
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-sm font-medium">{title}</div>
        {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function TipBox({ title, value, sub }: { title: string; value: string; sub: string }) {
  return (
    <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md">
      <div className="font-medium">{title}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
      <div className="text-muted-foreground">{sub}</div>
    </div>
  );
}

/** Бар-чарт заказов по месяцам; последние два месяца выделены (порт drawBars). */
export function OrdersBarChart({
  rows,
  ctx,
}: {
  rows: ViewRow[];
  ctx: ViewContext;
}): React.ReactElement {
  const isDark = useIsDark();
  const data = useMemo(() => {
    const totals = ctx.months.map((_, i) => rows.reduce((s, r) => s + (r.qty[i] ?? 0), 0));
    const periodTotal = totals.reduce((s, v) => s + v, 0);
    return ctx.months.map((month, i) => ({
      month,
      label: monthLabel(month),
      value: totals[i] ?? 0,
      share: periodTotal ? ((totals[i] ?? 0) / periodTotal) * 100 : 0,
    }));
  }, [rows, ctx.months]);

  // Один тон с акцентом свежести: старые месяцы приглушены, последние два — ярче
  const fills = isDark
    ? { old: "#334155", prev: "#0369a1", last: "#38bdf8" }
    : { old: "#cbd5e1", prev: "#7dd3fc", last: "#0284c7" };

  return (
    <ChartCard title="Заказы по месяцам" sub={`${ctx.months.length} мес.`}>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} strokeOpacity={0.25} />
          <XAxis
            dataKey="label"
            tick={axisTick(isDark)}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={axisTick(isDark)}
            tickLine={false}
            axisLine={false}
            width={52}
            tickFormatter={(v: number) => int0(v)}
          />
          <Tooltip
            cursor={{ fillOpacity: 0.06 }}
            content={({ active, payload }) => {
              const p = active && payload?.[0]?.payload;
              if (!p) return null;
              return (
                <TipBox
                  title={monthFullLabel(p.month)}
                  value={int0(p.value)}
                  sub={`заказов · ${p.share.toFixed(1).replace(".", ",")}% периода`}
                />
              );
            }}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={38}>
            {data.map((d, i) => (
              <Cell
                key={d.month}
                fill={
                  i === data.length - 1 ? fills.last : i === data.length - 2 ? fills.prev : fills.old
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/** Донат долей каналов + легенда с прямыми подписями (порт drawDonut). */
export function MpDonut({ rows }: { rows: ViewRow[] }): React.ReactElement {
  const isDark = useIsDark();
  const data = useMemo(() => {
    const sums: Record<MpCode, number> = { wb: 0, ozon: 0, ym: 0 };
    for (const r of rows) {
      sums.wb += r.mps.wb;
      sums.ozon += r.mps.ozon;
      sums.ym += r.mps.ym;
    }
    const total = Math.max(1, sums.wb + sums.ozon + sums.ym);
    return CHANNELS.map((ch) => ({
      key: ch.key,
      name: ch.name,
      color: isDark ? ch.dark : ch.light,
      value: sums[ch.key],
      share: (sums[ch.key] / total) * 100,
    }));
  }, [rows, isDark]);

  return (
    <ChartCard title="Каналы" sub="доля заказов за период">
      <div className="flex items-center gap-3">
        <ResponsiveContainer width="55%" height={200}>
          <PieChart>
            <Tooltip
              content={({ active, payload }) => {
                const p = active && payload?.[0]?.payload;
                if (!p) return null;
                return (
                  <TipBox
                    title={p.name}
                    value={int0(p.value)}
                    sub={`заказов · ${p.share.toFixed(1).replace(".", ",")}%`}
                  />
                );
              }}
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="55%"
              outerRadius="90%"
              paddingAngle={2}
              strokeWidth={0}
            >
              {data.map((d) => (
                <Cell key={d.key} fill={d.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-2">
          {data.map((d) => (
            <div key={d.key} className="flex items-center gap-2 text-xs">
              <span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: d.color }} />
              <span className="flex-1 truncate">{d.name}</span>
              <span className="font-medium tabular-nums">
                {d.share.toFixed(1).replace(".", ",")}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}

interface InsightRow {
  name: string;
  value: number;
  width: number; // % ширины трека
  suffix?: string;
  color: string;
}

function InsightCard({
  title,
  items,
  empty,
  deltaColors,
}: {
  title: string;
  items: InsightRow[];
  empty: string;
  deltaColors?: boolean;
}): React.ReactElement {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="mb-2 text-sm font-medium">{title}</div>
      {items.length === 0 && <div className="text-xs text-muted-foreground">{empty}</div>}
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.name} className="text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate" title={item.name}>{item.name}</span>
              <span
                className={cn(
                  "shrink-0 font-medium tabular-nums",
                  deltaColors &&
                    (item.value >= 0
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-red-600 dark:text-red-400"),
                )}
              >
                {deltaColors ? signed(item.value) : int0(item.value)}
                {item.suffix ?? ""}
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded bg-muted">
              <div
                className="h-full rounded"
                style={{ width: `${item.width}%`, backgroundColor: item.color }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Блок «Инсайты»: рост / просадка / риск остатков / каналы (порт renderInsights). */
export function Insights({
  rows,
  ctx,
}: {
  rows: ViewRow[];
  ctx: ViewContext;
}): React.ReactElement {
  const isDark = useIsDark();
  const { growth, drop, risk, channels } = useMemo(() => {
    const enriched = rows.map((row) => ({ row, m: rowMetrics(row, ctx) }));
    const growth = enriched
      .filter((x) => x.m.deltaMtd > 0)
      .sort((a, b) => b.m.deltaMtd - a.m.deltaMtd)
      .slice(0, 5);
    const drop = enriched
      .filter((x) => x.m.deltaMtd < 0)
      .sort((a, b) => a.m.deltaMtd - b.m.deltaMtd)
      .slice(0, 5);
    const risk = enriched
      .filter((x) => x.row.stockTotal > 0 && x.m.stockDays !== null && x.m.stockDays > 0)
      .sort((a, b) => (a.m.stockDays ?? 0) - (b.m.stockDays ?? 0))
      .slice(0, 5);
    const sums: Record<MpCode, number> = { wb: 0, ozon: 0, ym: 0 };
    for (const r of rows) {
      sums.wb += r.mps.wb;
      sums.ozon += r.mps.ozon;
      sums.ym += r.mps.ym;
    }
    const total = Math.max(1, sums.wb + sums.ozon + sums.ym);
    const channels = CHANNELS.map((ch) => ({
      name: ch.name,
      value: (sums[ch.key] / total) * 100,
      width: (sums[ch.key] / total) * 100,
      color: isDark ? ch.dark : ch.light,
    })).sort((a, b) => b.value - a.value);
    return { growth, drop, risk, channels };
  }, [rows, ctx, isDark]);

  const track = (vals: number[]): ((v: number) => number) => {
    const max = Math.max(1, ...vals.map((v) => Math.abs(v)));
    return (v: number) => Math.max(4, Math.round((Math.abs(v) / max) * 100));
  };
  const gw = track(growth.map((x) => x.m.deltaMtd));
  const dw = track(drop.map((x) => x.m.deltaMtd));
  const rw = track(risk.map((x) => x.m.stockDays ?? 0));

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <InsightCard
        title="Лидеры роста (Δ MTD)"
        empty="Нет роста"
        deltaColors
        items={growth.map((x) => ({
          name: `${x.row.article} · ${x.row.name}`,
          value: x.m.deltaMtd,
          width: gw(x.m.deltaMtd),
          color: "#10b981",
        }))}
      />
      <InsightCard
        title="Просадка (Δ MTD)"
        empty="Нет просадки"
        deltaColors
        items={drop.map((x) => ({
          name: `${x.row.article} · ${x.row.name}`,
          value: x.m.deltaMtd,
          width: dw(x.m.deltaMtd),
          color: "#ef4444",
        }))}
      />
      <InsightCard
        title="Риск остатков (запас, дн)"
        empty="Нет данных по риску"
        items={risk.map((x) => ({
          name: `${x.row.article} · ${x.row.name}`,
          value: x.m.stockDays ?? 0,
          width: rw(x.m.stockDays ?? 0),
          suffix: " дн.",
          color: "#f59e0b",
        }))}
      />
      <InsightCard
        title="Каналы"
        empty="Нет данных"
        items={channels.map((c) => ({
          name: c.name,
          value: Math.round(c.value * 10) / 10,
          width: c.width,
          suffix: "%",
          color: c.color,
        }))}
      />
    </div>
  );
}
